import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Home, BarChart3, Database, Settings, LogOut, Menu, X, ExternalLink, Upload, Users, Download, History, Copy, Mail, Settings2, BookOpen, Trophy, Activity, Webhook as WebhookIcon } from 'lucide-react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

export default function DashboardLayout({ children }: { children: React.ReactNode }) {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { signOut, adminRole } = useAdminAuth();
    const [isLoggingOut, setIsLoggingOut] = useState(false);

    // Set document title for admin dashboard
    useEffect(() => {
        document.title = 'Dashboard Admin IKAHATA';
    }, []);

    // Lock body scroll on mobile when sidebar is open
    // 📱 Mobile UX & Responsiveness
    // - Body Scroll Lock: The main page content is now locked when the mobile sidebar is open, preventing accidental background scrolling.
    useEffect(() => {
        if (sidebarOpen && window.innerWidth < 1024) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [sidebarOpen]);

    const handleLogout = async () => {
        if (isLoggingOut) return;
        setIsLoggingOut(true);
        try {
            await signOut();
            navigate('/admin/login');
        } catch (error) {
            console.error('Logout failed:', error);
            setIsLoggingOut(false);
        }
    };

    const menuItems = [
        { path: '/admin', label: 'Dashboard', icon: Home },
        { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
        { path: '/admin/alumni', label: 'Database Mentor', icon: Database },
        { path: '/admin/tryouts', label: 'Kelola Tryout', icon: BookOpen },
        { path: '/admin/leaderboard', label: 'Leaderboard', icon: Trophy },
        { path: '/admin/import', label: 'Import CSV', icon: Upload },
        { path: '/admin/export', label: 'Export Data', icon: Download },
        { path: '/admin/duplicates', label: 'Cek Duplikat', icon: Copy },
        { path: '/admin/bulk', label: 'Bulk Operations', icon: Settings2 },
        { path: '/admin/email', label: 'Email & Broadcast', icon: Mail },
        { path: '/admin/activity', label: 'Admin Activity', icon: History },
        { path: '/admin/user-activity', label: 'User Activity', icon: Activity },
        { path: '/admin/users', label: 'User Admin', icon: Users },
        { path: '/admin/webhooks', label: 'Discord Webhooks', icon: WebhookIcon },
        { path: '/admin/settings', label: 'Settings', icon: Settings },
    ];

    const isActive = (path: string) => {
        if (path === '/admin') return location.pathname === '/admin';
        return location.pathname.startsWith(path);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white flex flex-col md:flex-row overflow-x-hidden w-full max-w-full">
            <div className="flex flex-1 w-full max-w-full">
                {/* Sidebar - Desktop */}
                <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-slate-800 transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-transform duration-200 ease-in-out flex flex-col`}>
                    <div className="flex flex-col h-full h-[100dvh]">
                        {/* Logo */}
                        <div className="p-4 border-b border-slate-700 pt-[calc(1rem+env(safe-area-inset-top))] lg:pt-4">
                            <div className="flex items-center justify-between">
                                <h2 className="text-xl font-bold">🛡️ Admin Panel</h2>
                                <button className="lg:hidden" onClick={() => setSidebarOpen(false)}>
                                    <X size={24} />
                                </button>
                            </div>
                        </div>

                        {/* Navigation */}
                        <nav className="flex-1 p-4 space-y-1 overflow-y-auto overscroll-contain custom-scrollbar">
                            {menuItems.map((item, index) => {
                                // Add item directly to prevent React warning if menuItems array is modified later
                                const itemContent = (
                                    <Link
                                        key={item.path || String(index)}
                                        to={item.path}
                                        onClick={() => setSidebarOpen(false)}
                                        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition ${isActive(item.path)
                                            ? 'bg-indigo-600 text-white'
                                            : 'text-slate-300 hover:bg-slate-700'
                                            } `}
                                    >
                                        <item.icon size={20} />
                                        {item.label}
                                    </Link>
                                );
                                return itemContent;
                            })}
                        </nav>

                        {/* Bottom Actions */}
                        <div className="p-4 border-t border-slate-700 space-y-2 pb-[calc(1rem+env(safe-area-inset-bottom))] lg:pb-4">
                            <Link
                                to="/"
                                className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-slate-700 transition"
                            >
                                <ExternalLink size={20} />
                                Lihat Website
                            </Link>
                            <button
                                onClick={handleLogout}
                                disabled={isLoggingOut}
                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-400 hover:bg-red-600/20 transition-all active:scale-[0.98] ${isLoggingOut ? 'opacity-50 cursor-wait' : ''} `}
                            >
                                {isLoggingOut ? (
                                    <div className="w-5 h-5 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                                ) : (
                                    <LogOut size={20} />
                                )}
                                {isLoggingOut ? 'Logging out...' : 'Logout'}
                            </button>
                        </div>
                    </div>
                </aside>

                {/* Overlay for mobile */}
                {sidebarOpen && (
                    <div
                        className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                        onClick={() => setSidebarOpen(false)}
                    />
                )}

                {/* Main Content */}
                <main className="flex-1 min-w-0 min-h-screen max-w-full overflow-hidden flex flex-col">
                    {/* Top Header */}
                    <header className="bg-slate-900/50 backdrop-blur-sm sticky top-0 z-30 px-4 py-3 flex items-center justify-between border-b border-slate-700">
                        <button
                            className="lg:hidden p-2 hover:bg-slate-700 rounded-lg"
                            onClick={() => setSidebarOpen(true)}
                        >
                            <Menu size={24} />
                        </button>
                        <div className="flex items-center gap-2 text-sm text-slate-400">
                            <span className="hidden sm:inline">AMP IKAHATA</span>
                            <span className="text-slate-600">•</span>
                            <span className="text-indigo-400 font-bold uppercase tracking-wider">
                                {adminRole?.replace('_', ' ') || 'Admin'}
                            </span>
                        </div>
                        <div className="w-8 lg:hidden" /> {/* Spacer for alignment */}
                    </header>

                    {/* Page Content - with smooth fade in */}
                    <div className="min-h-[calc(100vh-57px)] animate-fadeIn">
                        {children}
                    </div>
                </main>
            </div>
        </div>
    );
}
